import React from 'react';
import PropTypes from 'prop-types';

import {
  Box, Stack, Collapse, Typography, Button, Divider, Card, CardContent,
  FreeSoloDropdown, TextField, SwitchCheckbox, Chip, Dropdown, DropdownMenuItem,
} from 'views/components';
import {DatePickerYearMonth, Translate, translate} from 'views/module';
import {Delete, CheckCircle} from 'views/icons';

import moment from 'moment';

const CertificationsSection = (props) => {
  const {
    certifications,
    onCertificationsChange,
    setValidity,
    setDisableNext,
  } = props;

  const DEFAULT_CERTIFICATION_DETAIL = {
    type: null,
    issuingOrganization: '',
    country: 'CA',
    provinceState: '',
    city: '',
    currentlyInProgress: false,
    yearObtainedOrExpected: '',
    licenseNumber: '',
    expiryDate: '',
  };

  const [state, setState] = React.useState('CHIP'); // CHIP or FORM
  const [isFormValid, setIsFormValid] = React.useState(false);
  const [certificationDetails, setCertificationDetails] = React.useState(DEFAULT_CERTIFICATION_DETAIL);

  // Update validity whenever certifications change
  React.useEffect(() => {
    setValidity(certifications.length > 0);
  }, [certifications, setValidity]);

  // Disable when the system in form state
  React.useEffect(() => {
    setDisableNext(state === 'FORM');
  }, [state]);

  // Update validity of form when certification details change
  React.useEffect(() => {
    setIsFormValid(
        Boolean(
            certificationDetails.issuingOrganization?.trim() &&
            certificationDetails.country &&
            certificationDetails.provinceState &&
            certificationDetails.city?.trim() &&
            certificationDetails.yearObtainedOrExpected,
        ),
    );
  }, [certificationDetails]);

  // Getting translation group for countries and ceritifcation
  const {countries, certificationOptions} = React.useMemo(() => {
    const countries = Object.keys(translate('caregiverScreening.Certifications.AvailableCountries'));
    const certificationOptions = Object.keys(translate('caregiverScreening.Certifications.AvailableCertifications'));
    return {countries, certificationOptions};
  }, []);

  const getCertificationLabel = (certificationId) => translate(`caregiverScreening.Certifications.AvailableCertifications.${certificationId}`);
  const getCountryLabel = (countryId) => translate(`caregiverScreening.Certifications.AvailableCountries.${countryId}`);

  const getProvincesOrStates = (selectedCountry) => {
    if (selectedCountry === 'CA') {
      const provinces = translate('caregiverScreening.Certifications.SubdivisionCA');
      const provincesAndTerritories = Object.entries(provinces).map(([key, value]) => ({
        value: key,
        label: translate(`caregiverScreening.Certifications.SubdivisionCA.${key}`),
      }));
      return provincesAndTerritories;
    } else if (selectedCountry === 'US') {
      const stateList = translate('caregiverScreening.Certifications.SubdivisionUS');
      const states = Object.entries(stateList).map(([key, value]) => ({
        value: key,
        label: translate(`caregiverScreening.Certifications.SubdivisionUS.${key}`),
      }));
      return states;
    } else {
      return [];
    }
  };

  const handleCertificationClick = (certification) => {
    const certificationPreData = certifications.find((c) => c.type === certification);
    if (certificationPreData) { // If it is already populated, allow them to edit
      setCertificationDetails(() => {
        return {
          ...certificationPreData, // pre set it based on the old answers
        };
      });
    } else { // This is a new selection, set them up to create the new certification from scratch
      setCertificationDetails(() => {
        return {
          ...DEFAULT_CERTIFICATION_DETAIL, // currentCertificationDetail should be default here
          type: certification,
        };
      });
    }

    setState('FORM');
  };

  const handleSaveCertification = () => {
    const oldCertifications = certifications.filter((certification) => certification.type !== certificationDetails.type);

    const newCertifications = [
      ...oldCertifications,
      certificationDetails,
    ];
    onCertificationsChange(newCertifications);
    cancelForm();
  };

  const handleRemoveCertification = (certification) => {
    const updatedCertifications = certifications.filter((c) => c.type !== certification);
    onCertificationsChange(updatedCertifications);
  };

  const cancelForm = () => {
    setState('CHIP');
    setCertificationDetails(DEFAULT_CERTIFICATION_DETAIL);
  };

  return (
    <Box sx={{p: 3}}>
      {/* Certifications Section */}
      <Box>
        {/* Header */}
        <Typography variant="h5" gutterBottom align='center' fontWeight={600}>
          <Translate expression='caregiverScreening.Certifications.Title'/>
        </Typography>
        <Typography variant="body2" color="text.secondary" align='center'>
          <Translate expression='caregiverScreening.Certifications.Subtitle'/>
        </Typography>


        {/* Displays the chips to select from */}
        {
          state === 'CHIP' &&
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 1,
              mt: 2,
            }}
          >
            {
              certificationOptions.map((certification) => {
                const isCertificationSelected = certifications.some((e) => e.type === certification);

                return (
                  <Chip
                    key={certification}
                    label={getCertificationLabel(certification)}
                    onClick={() => handleCertificationClick(certification)}
                    color={isCertificationSelected ? 'secondary' : 'default'}
                    variant={isCertificationSelected ? 'filled' : 'outlined'}
                    sx={{
                      'cursor': 'pointer',
                      'transition': 'all 0.2s ease-in-out',
                      'boxShadow': isCertificationSelected ? 2 : 0,
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: 2,
                      },
                    }}
                  />
                );
              })
            }
          </Box>
        }

        {/* Displays the form to fill out */}
        {
          state === 'FORM' &&
          <Card sx={{boxShadow: 2, borderRadius: 2, p: 0, mt: 2, mb: 2}}>
            <Box sx={{pt: 2, pl: 2, pr: 2}}>
              <Typography variant="h6" fontWeight="bold">
                {getCertificationLabel(certificationDetails.type)}
              </Typography>
              <Divider sx={{mt: 1}} />
            </Box>

            <CardContent sx={{p: {xs: 1, sm: 2}}}>
              <Stack spacing={2}>
                <Box>
                  <Typography color="text.secondary" variant="subtitle2" sx={{mb: 1}}>
                    {translate('caregiverScreening.Certifications.IssuingOrgOrSchool')}
                  </Typography>
                  <FreeSoloDropdown
                    options={translate('caregiverScreening.Certifications.AvailableOrgsAndSchools')}
                    value={certificationDetails.issuingOrganization}
                    onChange={(newValue) => {
                      setCertificationDetails({...certificationDetails, issuingOrganization: newValue});
                    }}
                    placeholder={translate('caregiverScreening.Certifications.IssuingOrgOrSchoolPlaceholder')}
                    fullWidth
                    required
                    size="small"
                  />
                </Box>

                {/* Location section */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr 1fr'},
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography color="text.secondary" variant="subtitle2" sx={{mb: 1}}>
                      {translate('caregiverScreening.Certifications.Country')}
                    </Typography>
                    <Dropdown
                      value={certificationDetails.country}
                      onChange={(newValue) => {
                        setCertificationDetails({...certificationDetails, country: newValue, provinceState: null});
                      }}
                      dropdownSx={{
                        '.MuiSelect-select': {
                          padding: '8px 12px',
                        },
                      }}
                    >
                      {
                        countries.map((country) => (
                          <DropdownMenuItem key={country} value={country}>
                            {getCountryLabel(country)}
                          </DropdownMenuItem>
                        ))
                      }
                    </Dropdown>
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="subtitle2" sx={{mb: 1}}>
                      {translate('caregiverScreening.Certifications.ProvinceOrState')}
                    </Typography>
                    <Dropdown
                      value={certificationDetails.provinceState || ''}
                      onChange={(newValue) => setCertificationDetails({...certificationDetails, provinceState: newValue})}
                      dropdownSx={{
                        '.MuiSelect-select': {
                          padding: '8px 12px',
                        },
                      }}
                    >
                      {
                        getProvincesOrStates(certificationDetails.country).map((option) => (
                          <DropdownMenuItem key={option.value} value={option.value}>
                            {option.label}
                          </DropdownMenuItem>
                        ))
                      }
                    </Dropdown>
                  </Box>
                  <Box>
                    <Typography color="text.secondary" variant="subtitle2" sx={{mb: 1}}>
                      {translate('caregiverScreening.Certifications.City')}
                    </Typography>
                    <TextField
                      placeholder={translate('caregiverScreening.Certifications.CityPlaceholder')}
                      value={certificationDetails.city}
                      onChange={(newValue) => setCertificationDetails({...certificationDetails, city: newValue})}
                      fullWidth
                      required
                      size="small"
                    />
                  </Box>
                </Box>

                <SwitchCheckbox
                  value={certificationDetails.currentlyInProgress}
                  onChange={(newValue) => setCertificationDetails({...certificationDetails, currentlyInProgress: newValue, yearObtainedOrExpected: null})}
                  label={translate('caregiverScreening.Certifications.CurrentlyInProgress')}
                />

                <Box>
                  <Typography color="text.secondary" variant="subtitle2" sx={{mb: 1}}>
                    {
                      certificationDetails.currentlyInProgress ?
                      translate('caregiverScreening.Certifications.YearExpected') :
                      translate('caregiverScreening.Certifications.YearObtained')
                    }
                  </Typography>
                  <Dropdown
                    value={certificationDetails.yearObtainedOrExpected}
                    onChange={(newValue) => setCertificationDetails({...certificationDetails, yearObtainedOrExpected: newValue})}
                    dropdownSx={{
                      '.MuiSelect-select': {
                        padding: '8px 12px',
                      },
                    }}
                    fullWidth
                    size="small"
                  >
                    {
                      certificationDetails.currentlyInProgress ?
                        Array.from({length: 15}, (_, i) => new Date().getFullYear() + i).map((year) => (
                          <DropdownMenuItem key={year} value={year}>{year}</DropdownMenuItem>
                        )) :
                        Array.from({length: 60}, (_, i) => new Date().getFullYear() - i).map((year) => (
                          <DropdownMenuItem key={year} value={year}>{year}</DropdownMenuItem>
                        ))
                    }
                  </Dropdown>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'},
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography color="text.secondary" variant="subtitle2" sx={{mb: 1}}>
                      {translate('caregiverScreening.Certifications.LicenseOrRegistrationNumber')}
                    </Typography>
                    <TextField
                      placeholder="e.g., 12345"
                      value={certificationDetails.licenseNumber}
                      onChange={(newValue) => setCertificationDetails({...certificationDetails, licenseNumber: newValue})}
                      fullWidth
                      size="small"
                    />
                  </Box>

                  <Box>
                    <Typography color="text.secondary" variant="subtitle2" sx={{mb: 1}}>
                      {translate('caregiverScreening.Certifications.ExpiryDate')}
                    </Typography>
                    <DatePickerYearMonth
                      value={certificationDetails.expiryDate && certificationDetails.expiryDate.trim() ? moment(certificationDetails.expiryDate + '-01') : null}
                      onChange={(newValue) => {
                        const dateString = newValue && typeof newValue.isValid === 'function' && newValue.isValid() ? newValue.format('YYYY-MM') : '';
                        setCertificationDetails({...certificationDetails, expiryDate: dateString});
                      }}
                      textFieldProps={{
                        fullWidth: true,
                        size: 'small',
                      }}
                    />
                  </Box>
                </Box>

                <Box sx={{p: 1}}>
                  <Divider />
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'},
                    gap: 2,
                  }}
                >
                  {/* Remove button */}
                  <Box sx={{display: 'flex', justifyContent: 'flex-start'}}>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => handleRemoveCertification(certificationDetails.type) & cancelForm()}
                      size="small"
                    >
                      <Translate expression='caregiverScreening.Certifications.RemoveButton'/>
                    </Button>
                  </Box>

                  {/* Save button */}
                  <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => handleSaveCertification()}
                      disabled={!isFormValid}
                      size="small"
                    >
                      <Translate expression='caregiverScreening.Certifications.SaveButton'/>
                    </Button>
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        }
      </Box>

      {/* Certifications Review */}
      <Collapse in={certifications.length > 0}>
        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: 'action.hover',
            borderRadius: 1,
          }}
        >
          {/* Certifications Summary */}
          {
            certifications.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom>
                  <Translate expression='caregiverScreening.Certifications.SelectedCount' substitute={{count: certifications.length}}/>
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    mb: certifications.length > 0 ? 2 : 0,
                  }}
                >
                  {
                    certifications.map((certification) => (
                      <Box
                        key={`selected-cert-${certification.type}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 2,
                          border: '1px solid',
                          borderColor: 'grey.300',
                          borderRadius: 2,
                          backgroundColor: 'background.default',
                          position: 'relative',
                        }}
                      >
                        {/* Checkmark icon on the left */}
                        <Box sx={{display: 'flex', alignItems: 'center', mr: {xs: 1, lg: 2}}}>
                          <CheckCircle sx={{color: 'success.light', fontSize: 24}} />
                        </Box>
                        <Box sx={{flex: 1}}>
                          <Typography variant="body1" fontWeight="bold">
                            {translate(`caregiverScreening.Certifications.AvailableCertifications.${certification.type}`)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {certification.issuingOrganization} {/* User supplied */}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {certification.yearObtainedOrExpected}
                            {' \u2022 '}
                            {translate(`caregiverScreening.Certifications.AvailableCountries.${certification.country}`)}
                            {' \u2022 '}
                            {translate(`caregiverScreening.Certifications.Subdivision${certification.country}.${certification.provinceState}`)}
                            {' \u2022 '}
                            {certification.city}
                          </Typography>
                        </Box>

                        <Box sx={{}}>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => handleRemoveCertification(certification.type)}
                            sx={{
                              'minWidth': 0,
                              'p': 0.5,
                              'borderRadius': 1,
                              'color': 'grey.500',
                              'backgroundColor': 'transparent',
                              'boxShadow': 'none',
                              '&:hover': {
                                backgroundColor: 'transparent',
                                color: 'grey.700',
                              },
                            }}
                          >
                            <Delete />
                          </Button>
                        </Box>
                      </Box>
                    ))
                  }
                </Box>
              </>
            )
          }
        </Box>
      </Collapse>
    </Box>
  );
};

CertificationsSection.propTypes = {
  onCertificationsChange: PropTypes.func.isRequired,
  certifications: PropTypes.arrayOf(PropTypes.object).isRequired,
  setValidity: PropTypes.func.isRequired,
  setDisableNext: PropTypes.func.isRequired,
};

export default CertificationsSection;
